import axios from 'axios';
import {
  IAdminBillingOverview,
  IAppliedDiscount,
  IBillingSetupRequestState,
  IBillingStatus,
  IBillingUpgradePreview,
  ICouponList,
  ICouponSummary,
  ICreateCouponPayload,
  IRequestBillingSetupPayload,
} from '../Types/onboarding';
import { formatSubscriptionEndLabel } from '../utils/billingSubscription';

const BASE = import.meta.env.VITE_API_BASE_URL;

function authHeaders() {
  const raw = localStorage.getItem('user');
  if (!raw) return {};
  try {
    const user = JSON.parse(raw);
    return { Authorization: `Bearer ${user.access_token}` };
  } catch {
    return {};
  }
}

export async function fetchBillingStatus(clientId: string): Promise<IBillingStatus> {
  const { data } = await axios.get<IBillingStatus>(`${BASE}/billing/status`, {
    params: { client_id: clientId },
    headers: authHeaders(),
  });
  return data;
}

export async function skipTrial(clientId: string): Promise<string> {
  const { data } = await axios.post<{ message: string }>(
    `${BASE}/billing/skip-trial`,
    null,
    { params: { client_id: clientId }, headers: authHeaders() }
  );
  return data.message;
}

export async function cancelSubscription(clientId: string): Promise<string> {
  const { data } = await axios.post<{ message: string }>(
    `${BASE}/billing/cancel`,
    null,
    { params: { client_id: clientId }, headers: authHeaders() }
  );
  return data.message;
}

export async function keepSubscription(clientId: string): Promise<string> {
  const { data } = await axios.post<{ message: string }>(
    `${BASE}/billing/keep-subscription`,
    null,
    { params: { client_id: clientId }, headers: authHeaders() }
  );
  return data.message;
}

export async function fetchUpgradePreview(clientId: string): Promise<IBillingUpgradePreview> {
  const { data } = await axios.get<IBillingUpgradePreview>(`${BASE}/billing/upgrade-preview`, {
    params: { client_id: clientId },
    headers: authHeaders(),
  });
  return data;
}

export async function upgradeSubscription(clientId: string): Promise<string> {
  const { data } = await axios.post<{ message: string }>(
    `${BASE}/billing/upgrade`,
    null,
    { params: { client_id: clientId }, headers: authHeaders() }
  );
  return data.message;
}

export async function openBillingPortal(clientId: string): Promise<string> {
  const { data } = await axios.get<{ url: string }>(`${BASE}/billing/portal`, {
    params: { client_id: clientId },
    headers: authHeaders(),
  });
  return data.url;
}

export async function reactivateSubscription(clientId: string): Promise<string> {
  const { data } = await axios.post<{ checkout_url: string }>(
    `${BASE}/billing/reactivate`,
    null,
    { params: { client_id: clientId }, headers: authHeaders() }
  );
  return data.checkout_url;
}

export async function createBillingSetupCheckout(clientId: string): Promise<string> {
  const { data } = await axios.post<{ checkout_url: string }>(
    `${BASE}/billing/setup`,
    null,
    { params: { client_id: clientId }, headers: authHeaders() }
  );
  return data.checkout_url;
}

export async function fetchBillingSetupRequest(
  clientId: string
): Promise<IBillingSetupRequestState> {
  const { data } = await axios.get<IBillingSetupRequestState>(
    `${BASE}/billing/setup-request`,
    { params: { client_id: clientId }, headers: authHeaders() }
  );
  return data;
}

export async function getBillingSetupRequest(
  clientId: string
): Promise<IBillingSetupRequestState> {
  const { data } = await axios.get<IBillingSetupRequestState>(
    `${BASE}/billing/admin/clients/${clientId}/request-billing`,
    { headers: authHeaders() }
  );
  return data;
}

export async function requestBillingSetup(
  clientId: string,
  payload: IRequestBillingSetupPayload
): Promise<IBillingSetupRequestState> {
  const { data } = await axios.post<IBillingSetupRequestState>(
    `${BASE}/billing/admin/clients/${clientId}/request-billing`,
    payload,
    { headers: authHeaders() }
  );
  return data;
}

export async function cancelBillingSetupRequest(
  clientId: string
): Promise<IBillingSetupRequestState> {
  const { data } = await axios.delete<IBillingSetupRequestState>(
    `${BASE}/billing/admin/clients/${clientId}/request-billing`,
    { headers: authHeaders() }
  );
  return data;
}

export async function getAdminBillingOverview(): Promise<IAdminBillingOverview> {
  const { data } = await axios.get<IAdminBillingOverview>(
    `${BASE}/billing/admin/overview`,
    { headers: authHeaders() }
  );
  return data;
}

export async function listCoupons(): Promise<ICouponList> {
  const { data } = await axios.get<ICouponList>(`${BASE}/billing/admin/coupons`, {
    headers: authHeaders(),
  });
  return data;
}

export async function createCoupon(
  payload: ICreateCouponPayload
): Promise<ICouponSummary> {
  const { data } = await axios.post<ICouponSummary>(
    `${BASE}/billing/admin/coupons`,
    payload,
    { headers: authHeaders() }
  );
  return data;
}

export async function retireCoupon(couponId: string): Promise<string> {
  const { data } = await axios.post<{ message: string }>(
    `${BASE}/billing/admin/coupons/${encodeURIComponent(couponId)}/retire`,
    {},
    { headers: authHeaders() }
  );
  return data.message;
}

export async function unretireCoupon(couponId: string): Promise<string> {
  const { data } = await axios.post<{ message: string }>(
    `${BASE}/billing/admin/coupons/${encodeURIComponent(couponId)}/unretire`,
    {},
    { headers: authHeaders() }
  );
  return data.message;
}

export async function removeCouponCode(couponId: string): Promise<string> {
  const { data } = await axios.delete<{ message: string }>(
    `${BASE}/billing/admin/coupons/${encodeURIComponent(couponId)}/code`,
    { headers: authHeaders() }
  );
  return data.message;
}

export async function getClientDiscount(clientId: string): Promise<IAppliedDiscount> {
  const { data } = await axios.get<IAppliedDiscount>(
    `${BASE}/billing/admin/clients/${clientId}/discount`,
    { headers: authHeaders() }
  );
  return data;
}

export async function applyClientDiscount(
  clientId: string,
  couponId: string
): Promise<string> {
  const { data } = await axios.post<{ message: string }>(
    `${BASE}/billing/admin/clients/${clientId}/discount`,
    { coupon_id: couponId },
    { headers: authHeaders() }
  );
  return data.message;
}

export async function removeClientDiscount(clientId: string): Promise<string> {
  const { data } = await axios.delete<{ message: string }>(
    `${BASE}/billing/admin/clients/${clientId}/discount`,
    { headers: authHeaders() }
  );
  return data.message;
}

export function formatBillingError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') return detail.split('\n\n').pop() || detail;
  }
  return 'Billing request failed.';
}

export function isBillingNotConfiguredError(error: unknown): boolean {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string' && detail.includes('Billing is not configured')) {
      return true;
    }
  }
  return false;
}

export function formatBillingStatusLabel(status: IBillingStatus): string {
  const endLabel = formatSubscriptionEndLabel(status);
  if (endLabel) {
    return endLabel;
  }

  if (status.is_trialing) {
    const days =
      status.trial_days_remaining != null ? ` · ${status.trial_days_remaining}d left` : '';
    return `Trial${days}`;
  }

  return status.subscription_status.replace(/_/g, ' ');
}
